import React, { useState, useEffect, useMemo } from 'react';
import { Card, Form, InputNumber, Row, Col, Typography, Slider } from 'antd';

const { Title, Text } = Typography;

interface CreditCalculatorProps {
  style?: React.CSSProperties;
}

const CreditCalculator: React.FC<CreditCalculatorProps> = ({ style }) => {
  const [propertyValue, setPropertyValue] = useState<number | null>(null);
  const [downPaymentRp, setDownPaymentRp] = useState<number>(0);
  const [downPaymentPercent, setDownPaymentPercent] = useState<number>(10);
  const [interestRate, setInterestRate] = useState<number>(5);
  const [loanYears, setLoanYears] = useState<number>(15);

  // Synchronize DP fields when property value or percentage changes
  useEffect(() => {
    const currentPropertyValue = propertyValue || 0;
    const newDownPaymentRp = (currentPropertyValue * downPaymentPercent) / 100;
    setDownPaymentRp(newDownPaymentRp);
  }, [propertyValue, downPaymentPercent]);

  const handlePropertyValueChange = (value: number | null) => {
    setPropertyValue(value);
  };

  const handleDownPaymentRpChange = (value: number | null) => {
    const dpAmount = value || 0;
    setDownPaymentRp(dpAmount);
    if (propertyValue && propertyValue > 0) {
      const newPercent = (dpAmount / propertyValue) * 100;
      setDownPaymentPercent(parseFloat(newPercent.toFixed(2)));
    } else {
      setDownPaymentPercent(0);
    }
  };

  const handleDownPaymentPercentChange = (value: number | null) => {
    const dpPercent = value || 0;
    setDownPaymentPercent(dpPercent);
    // This will be recalculated by the useEffect
  };

  const handleInterestRateChange = (value: number | null) => {
    setInterestRate(value || 0);
  };

  const handleLoanYearsChange = (value: number | null) => {
    setLoanYears(value || 0);
  };

  const { principal, monthlyPayment } = useMemo(() => {
    const currentPropertyValue = propertyValue || 0;
    const principal = currentPropertyValue - downPaymentRp;
    if (principal <= 0 || interestRate <= 0 || loanYears <= 0) {
      return { principal, monthlyPayment: 0 };
    }

    const monthlyInterest = interestRate / 12 / 100;
    const totalMonths = loanYears * 12;

    if (monthlyInterest === 0) {
        return { principal, monthlyPayment: principal / totalMonths };
    }

    const payment =
      principal *
      (monthlyInterest * Math.pow(1 + monthlyInterest, totalMonths)) /
      (Math.pow(1 + monthlyInterest, totalMonths) - 1);

    return { principal, monthlyPayment: payment };
  }, [propertyValue, downPaymentRp, interestRate, loanYears]);

  const currencyFormatter = (value?: number | string) => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const currencyParser = (value?: string) => Number(value!.replace(/Rp\s?|(,*)/g, ''));

  return (
    <Card style={style}>
      <Title level={4}>Kalkulator Kredit</Title>
      <Row gutter={[32, 16]}>
        <Col xs={24} md={12}>
          <Form layout="vertical">
            <Form.Item label="Property Value">
              <InputNumber
                style={{ width: '100%' }}
                value={propertyValue}
                onChange={handlePropertyValueChange}
                formatter={currencyFormatter}
                parser={currencyParser}
                min={0}
                step={10000000}
              />
            </Form.Item>
            <Form.Item label={`Down Payment (${downPaymentPercent.toFixed(2)} %)`}>
              <InputNumber
                style={{ width: '100%' }}
                value={downPaymentRp}
                onChange={handleDownPaymentRpChange}
                formatter={currencyFormatter}
                parser={currencyParser}
                min={0}
                max={propertyValue || undefined}
                step={1000000}
              />
            </Form.Item>
             <Slider
                value={downPaymentPercent}
                onChange={handleDownPaymentPercentChange}
                min={0}
                max={100}
                step={0.5}
              />
            <Form.Item label="Interest (per tahun)">
              <InputNumber
                style={{ width: '100%' }}
                value={interestRate}
                onChange={handleInterestRateChange}
                addonAfter="%"
                min={0}
                max={100}
                step={0.1}
              />
            </Form.Item>
            <Form.Item label="Years (Tahun)">
              <InputNumber
                style={{ width: '100%' }}
                value={loanYears}
                onChange={handleLoanYearsChange}
                min={1}
                max={30}
                precision={0}
              />
            </Form.Item>
          </Form>
        </Col>
        <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingLeft: 32 }}>
            <div>
                <Text type="secondary">Principal</Text>
                <Title level={3} style={{ marginTop: 0 }}>{currencyFormatter(principal)}</Title>
            </div>
            <div style={{ marginTop: 24 }}>
                <Text type="secondary">Cicilan per Bulan</Text>
                <Title level={3} style={{ marginTop: 0, color: '#1890ff' }}>
                    {monthlyPayment > 0 ? currencyFormatter(Math.round(monthlyPayment)) : 'Rp 0'}
                </Title>
            </div>
        </Col>
      </Row>
    </Card>
  );
};

export default CreditCalculator;
